import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Edit2, Settings, UploadCloud, ChevronDown, ChevronUp } from 'lucide-react';
import { ImageCropperModal } from './ImageCropperModal';
import type { CategoryConfig } from '../types/competition';

interface CategoryManagerUIProps {
  categories: string[];
  categoriesConfig: Record<string, CategoryConfig>;
  onUpdate: (newCategories: string[], newCategoriesConfig: Record<string, CategoryConfig>) => void;
  competitionType: 'RANKING' | 'TOURNAMENT';
  classifications?: { id: string; name: string }[];
  initialExpandedCategory?: string | null;
}

const EMOJI_LIST = ['🎾', '🥇', '🥈', '🥉', '⭐', '🔥', '🏆', '🎯', '⚡', '🚀', '👑', '🌟', '💥', '👊', '🏅'];

export const CategoryManagerUI: React.FC<CategoryManagerUIProps> = ({ categories, categoriesConfig, onUpdate, competitionType, classifications, initialExpandedCategory }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  
  const [name, setName] = useState('');
  const [logo, setLogo] = useState('');
  
  const [tempConfig, setTempConfig] = useState<CategoryConfig>({});
  
  const [cropperImageSrc, setCropperImageSrc] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setName('');
    setLogo('');
    setTempConfig({});
    setIsAdding(false);
    setEditingCategory(null);
  };

  const startEdit = (cat: string) => {
    setName(cat);
    const conf = categoriesConfig[cat] || {};
    setLogo(conf.logoUrl || '');
    setTempConfig(JSON.parse(JSON.stringify(conf)));
    setEditingCategory(cat);
    setIsAdding(true);
  };

  useEffect(() => {
    if (initialExpandedCategory && categoriesConfig[initialExpandedCategory]) {
      startEdit(initialExpandedCategory);
    }
  }, [initialExpandedCategory]);

  const handleSave = () => {
    if (!name.trim()) return;

    const trimmedName = name.trim();
    let newCats = [...categories];
    let newConfigs = { ...categoriesConfig };

    const finalConfig: CategoryConfig = {
      ...tempConfig,
      logoUrl: logo
    };

    if (editingCategory) {
      if (editingCategory !== trimmedName) {
        newCats = newCats.map(c => c === editingCategory ? trimmedName : c);
        newConfigs[trimmedName] = finalConfig;
        delete newConfigs[editingCategory];
      } else {
        newConfigs[trimmedName] = finalConfig;
      }
    } else {
      if (!newCats.includes(trimmedName)) {
        newCats.push(trimmedName);
      }
      newConfigs[trimmedName] = finalConfig;
    }

    onUpdate(newCats, newConfigs);
    resetForm();
  };

  const handleDelete = (cat: string) => {
    const newCats = categories.filter(c => c !== cat);
    const newConfigs = { ...categoriesConfig };
    delete newConfigs[cat];
    onUpdate(newCats, newConfigs);
  };

  const handleCopyFrom = (sourceCat: string) => {
    if (!sourceCat || !categoriesConfig[sourceCat]) return;
    const srcConfig = categoriesConfig[sourceCat];
    setTempConfig({
      ...tempConfig,
      matchEngine: srcConfig.matchEngine ? JSON.parse(JSON.stringify(srcConfig.matchEngine)) : undefined,
      pointsConfig: srcConfig.pointsConfig ? JSON.parse(JSON.stringify(srcConfig.pointsConfig)) : undefined,
      resetPolicy: srcConfig.resetPolicy ? JSON.parse(JSON.stringify(srcConfig.resetPolicy)) : undefined,
      ladderRules: srcConfig.ladderRules ? JSON.parse(JSON.stringify(srcConfig.ladderRules)) : undefined,
      tournamentRules: srcConfig.tournamentRules ? JSON.parse(JSON.stringify(srcConfig.tournamentRules)) : undefined,
      linkedClassificationId: srcConfig.linkedClassificationId
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      setCropperImageSrc(base64);
    };
    reader.readAsDataURL(file);
    e.target.value = ''; // Reset input to allow selecting the same file again
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {categories.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {categories.map((cat) => (
            <div key={cat} style={{ display: 'flex', flexDirection: 'column', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', overflow: 'hidden' }}>
                    {categoriesConfig[cat]?.logoUrl ? (
                      categoriesConfig[cat].logoUrl!.startsWith('data:image') || categoriesConfig[cat].logoUrl!.startsWith('http') ? (
                        <img src={categoriesConfig[cat].logoUrl} alt={cat} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <span>{categoriesConfig[cat].logoUrl}</span>
                      )
                    ) : (
                      <Settings size={16} color="var(--primary-color)" />
                    )}
                  </div>
                  <span style={{ color: 'white', fontWeight: 600 }}>{cat}</span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => setExpandedCategory(expandedCategory === cat ? null : cat)} style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: '4px' }}>
                    {expandedCategory === cat ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  <button onClick={() => startEdit(cat)} style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: '4px' }}>
                    <Edit2 size={16} />
                  </button>
                  <button onClick={() => handleDelete(cat)} style={{ background: 'transparent', border: 'none', color: '#EF4444', cursor: 'pointer', padding: '4px' }}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              {expandedCategory === cat && (
                <div style={{ padding: '16px', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: 'bold', color: 'white', fontSize: '18px' }}>{cat}</span>
                  </div>
                  <p style={{ margin: 0, fontSize: '14px', color: 'rgba(255,255,255,0.6)' }}>
                    Formato: {categoriesConfig[cat]?.matchEngine?.scoreFormat || 'Melhor de 3 Sets (Padrão)'}
                  </p>
                  <button onClick={() => startEdit(cat)} style={{ alignSelf: 'flex-start', padding: '6px 12px', background: 'var(--primary-color)', color: 'white', borderRadius: '4px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                    Editar Configurações da Categoria
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', margin: 0, fontStyle: 'italic' }}>Nenhuma categoria cadastrada.</p>
      )}

      {isAdding ? (
        <div style={{ background: 'rgba(0,0,0,0.2)', padding: '16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4 style={{ margin: 0, color: 'white', fontSize: '18px' }}>{editingCategory ? `Editando: ${editingCategory}` : 'Nova Categoria'}</h4>
            {categories.length > 0 && !editingCategory && (
              <select 
                onChange={(e) => handleCopyFrom(e.target.value)}
                style={{ padding: '6px 12px', borderRadius: '6px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', outline: 'none', fontSize: '12px' }}
                defaultValue=""
              >
                <option value="" disabled style={{ color: 'black' }}>Copiar regras de...</option>
                {categories.map(c => <option key={c} value={c} style={{ color: 'black' }}>{c}</option>)}
              </select>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <h5 style={{ margin: 0, color: 'var(--primary-color)', fontSize: '14px' }}>Identificação</h5>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Nome</label>
                <input 
                  type="text" 
                  placeholder="Ex: Categoria A" 
                  style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Ícone / Logo</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input 
                    type="text" 
                    placeholder="URL ou Emoji" 
                    style={{ flex: 1, padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                    value={logo}
                    onChange={(e) => setLogo(e.target.value)}
                  />
                  <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileUpload} />
                  <button onClick={() => fileInputRef.current?.click()} style={{ padding: '0 12px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer' }}>
                    <UploadCloud size={18} />
                  </button>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                  {EMOJI_LIST.map(emoji => (
                    <button 
                      key={emoji}
                      onClick={() => setLogo(emoji)}
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', cursor: 'pointer', padding: '4px 8px', fontSize: '16px' }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <h5 style={{ margin: 0, color: 'var(--primary-color)', fontSize: '14px' }}>Regras de Partida</h5>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Formato de Placar</label>
                <select 
                  style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                  value={tempConfig.matchEngine?.scoreFormat || 'BEST_OF_3_REGULAR'}
                  onChange={(e) => setTempConfig({ ...tempConfig, matchEngine: { ...(tempConfig.matchEngine || {}), scoreFormat: e.target.value as any, advantageRule: tempConfig.matchEngine?.advantageRule || 'WITH_ADVANTAGE', walkoverRule: tempConfig.matchEngine?.walkoverRule || 'SCORE_6_0_6_0' }})}
                >
                  <option style={{ color: 'black' }} value="BEST_OF_3_REGULAR">Melhor de 3 Sets</option>
                  <option style={{ color: 'black' }} value="BEST_OF_3_SUPER_TIEBREAK">2 Sets + Super Tiebreak</option>
                  <option style={{ color: 'black' }} value="PRO_SET_8">Pro Set (8 Games)</option>
                  <option style={{ color: 'black' }} value="PRO_SET_9">Pro Set (9 Games)</option>
                  <option style={{ color: 'black' }} value="SINGLE_SET_6">Set Único (6 Games)</option>
                  <option style={{ color: 'black' }} value="FAST_4">Fast 4</option>
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Regra de Vantagem</label>
                <select 
                  style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                  value={tempConfig.matchEngine?.advantageRule || 'WITH_ADVANTAGE'}
                  onChange={(e) => setTempConfig({ ...tempConfig, matchEngine: { ...(tempConfig.matchEngine || { scoreFormat: 'BEST_OF_3_REGULAR', walkoverRule: 'SCORE_6_0_6_0' }), advantageRule: e.target.value as any }})}
                >
                  <option style={{ color: 'black' }} value="WITH_ADVANTAGE">Com Vantagem</option>
                  <option style={{ color: 'black' }} value="NO_AD">No-Ad (Ponto Decisivo)</option>
                </select>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <h5 style={{ margin: 0, color: 'var(--primary-color)', fontSize: '14px' }}>Integração com Ranking Geral</h5>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <select 
                style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                value={tempConfig.linkedClassificationId || ''}
                onChange={(e) => setTempConfig({ ...tempConfig, linkedClassificationId: e.target.value || null })}
              >
                <option style={{ color: 'black' }} value="">Nenhuma (Não pontua no geral)</option>
                {classifications?.map(c => (
                  <option key={c.id} style={{ color: 'black' }} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>


          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <h5 style={{ margin: 0, color: 'var(--primary-color)', fontSize: '14px' }}>Sistema de Pontuação (Partida)</h5>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Pontos por Vitória</label>
                <input 
                  type="number"
                  style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                  value={tempConfig.pointsConfig?.winner ?? 10}
                  onChange={(e) => setTempConfig({ ...tempConfig, pointsConfig: { ...(tempConfig.pointsConfig || {}), winner: Number(e.target.value) } as any })}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Pontos por Derrota</label>
                <input 
                  type="number"
                  style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                  value={tempConfig.pointsConfig?.loser ?? 2}
                  onChange={(e) => setTempConfig({ ...tempConfig, pointsConfig: { ...(tempConfig.pointsConfig || {}), loser: Number(e.target.value) } as any })}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Vitória por W.O. (Pontos)</label>
                <input 
                  type="number"
                  style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                  value={tempConfig.pointsConfig?.woWinner ?? 10}
                  onChange={(e) => setTempConfig({ ...tempConfig, pointsConfig: { ...(tempConfig.pointsConfig || {}), woWinner: Number(e.target.value) } as any })}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Derrota por W.O. (Pontos/Penalidade)</label>
                <input 
                  type="number"
                  style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                  value={tempConfig.pointsConfig?.woLoser ?? -5}
                  onChange={(e) => setTempConfig({ ...tempConfig, pointsConfig: { ...(tempConfig.pointsConfig || {}), woLoser: Number(e.target.value) } as any })}
                />
              </div>
            </div>
          </div>

          {tempConfig.linkedClassificationId && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h5 style={{ margin: 0, color: 'var(--primary-color)', fontSize: '14px' }}>Zerar Pontuação (Ciclo)</h5>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Política de Zeração / Defesa de Pontos</label>
                  <select 
                    style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                    value={tempConfig.resetPolicy?.policy ?? 'MANUAL_ONLY'}
                    onChange={(e) => setTempConfig({ ...tempConfig, resetPolicy: { ...tempConfig.resetPolicy, policy: e.target.value as any }})}
                  >
                    <option style={{ color: 'black' }} value="ATP_STYLE_12_MONTHS">Estilo ATP (Pontos expiram após 12 meses)</option>
                    <option style={{ color: 'black' }} value="ANNUAL_JAN_1ST">Anual (Zera todo dia 01 de Janeiro)</option>
                    <option style={{ color: 'black' }} value="CUSTOM_DATE_RANGE">Período Customizado (Zera em data específica)</option>
                    <option style={{ color: 'black' }} value="MANUAL_ONLY">Apenas Manual (Comando direto do Gestor)</option>
                    <option style={{ color: 'black' }} value="SAME_ROUND_PREVIOUS_YEAR">Zeramento de mesma rodada do ano anterior</option>
                    <option style={{ color: 'black' }} value="KEEP_LAST_X_STAGES">Por número de etapas (Descarte automático)</option>
                  </select>
                </div>
                {tempConfig.resetPolicy?.policy === 'CUSTOM_DATE_RANGE' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Data de Início</label>
                      <input 
                        type="date"
                        style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                        value={tempConfig.resetPolicy?.customStartDate ?? ''}
                        onChange={(e) => setTempConfig({ ...tempConfig, resetPolicy: { ...tempConfig.resetPolicy!, customStartDate: e.target.value }})}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Data Final (Zerar)</label>
                      <input 
                        type="date"
                        style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                        value={tempConfig.resetPolicy?.customEndDate ?? ''}
                        onChange={(e) => setTempConfig({ ...tempConfig, resetPolicy: { ...tempConfig.resetPolicy!, customEndDate: e.target.value }})}
                      />
                    </div>
                  </div>
                )}
                {tempConfig.resetPolicy?.policy === 'KEEP_LAST_X_STAGES' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Número de Etapas a Manter</label>
                    <input 
                      type="number"
                      min="1"
                      style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                      value={tempConfig.resetPolicy?.stagesToKeep ?? 6}
                      onChange={(e) => setTempConfig({ ...tempConfig, resetPolicy: { ...tempConfig.resetPolicy!, stagesToKeep: parseInt(e.target.value) || 6 }})}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {competitionType === 'TOURNAMENT' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h5 style={{ margin: 0, color: 'var(--primary-color)', fontSize: '14px' }}>Formato do Torneio</h5>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Tipo de Chave</label>
                  <select 
                    style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                    value={tempConfig.tournamentRules?.drawFormat || 'SINGLE_ELIMINATION'}
                    onChange={(e) => setTempConfig({ ...tempConfig, tournamentRules: { ...(tempConfig.tournamentRules || { tiebreakerPriority: ['WINS'], participantType: 'SINGLES' }), drawFormat: e.target.value as any }})}
                  >
                    <option style={{ color: 'black' }} value="SINGLE_ELIMINATION">Eliminatória Simples</option>
                    <option style={{ color: 'black' }} value="DOUBLE_ELIMINATION">Dupla Eliminação</option>
                    <option style={{ color: 'black' }} value="ROUND_ROBIN_PURE">Grupos</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Tipo de Disputa</label>
                  <select 
                    style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                    value={tempConfig.tournamentRules?.participantType ?? 'SINGLES'}
                    onChange={(e) => setTempConfig({ ...tempConfig, tournamentRules: { ...(tempConfig.tournamentRules || { tiebreakerPriority: ['WINS'] }), participantType: e.target.value as any } as any })}
                  >
                    <option style={{ color: 'black' }} value="SINGLES">Simples (1x1)</option>
                    <option style={{ color: 'black' }} value="DOUBLES">Duplas (2x2)</option>
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Criação de Partidas</label>
                  <select 
                    style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                    value={tempConfig.tournamentRules?.matchGenerationMode ?? 'MANAGER_ONLY'}
                    onChange={(e) => setTempConfig({ ...tempConfig, tournamentRules: { ...(tempConfig.tournamentRules || { tiebreakerPriority: ['WINS'] }), matchGenerationMode: e.target.value as any } as any })}
                  >
                    <option style={{ color: 'black' }} value="MANAGER_ONLY">Manual (Apenas Gestor)</option>
                    <option style={{ color: 'black' }} value="AUTO_DRAW">Sorteio Automático</option>
                  </select>
                </div>
              </div>
              
              <div style={{ marginTop: '16px' }}>
                <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '8px' }}>Pontuação por Etapa Alcançada</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>Campeão</label>
                    <input type="number" style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }} value={tempConfig.tournamentRules?.stagePoints?.champion ?? 100} onChange={(e) => setTempConfig({ ...tempConfig, tournamentRules: { ...(tempConfig.tournamentRules || { tiebreakerPriority: ['WINS'] }), stagePoints: { ...(tempConfig.tournamentRules?.stagePoints || {}), champion: Number(e.target.value) } } as any })} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>Vice</label>
                    <input type="number" style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }} value={tempConfig.tournamentRules?.stagePoints?.finalist ?? 70} onChange={(e) => setTempConfig({ ...tempConfig, tournamentRules: { ...(tempConfig.tournamentRules || { tiebreakerPriority: ['WINS'] }), stagePoints: { ...(tempConfig.tournamentRules?.stagePoints || {}), finalist: Number(e.target.value) } } as any })} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>Semis</label>
                    <input type="number" style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }} value={tempConfig.tournamentRules?.stagePoints?.semiFinalist ?? 40} onChange={(e) => setTempConfig({ ...tempConfig, tournamentRules: { ...(tempConfig.tournamentRules || { tiebreakerPriority: ['WINS'] }), stagePoints: { ...(tempConfig.tournamentRules?.stagePoints || {}), semiFinalist: Number(e.target.value) } } as any })} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>Quartas</label>
                    <input type="number" style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }} value={tempConfig.tournamentRules?.stagePoints?.quarterFinalist ?? 20} onChange={(e) => setTempConfig({ ...tempConfig, tournamentRules: { ...(tempConfig.tournamentRules || { tiebreakerPriority: ['WINS'] }), stagePoints: { ...(tempConfig.tournamentRules?.stagePoints || {}), quarterFinalist: Number(e.target.value) } } as any })} />
                  </div>
                </div>
              </div>

              {tempConfig.tournamentRules?.drawFormat === 'DOUBLE_ELIMINATION' && (
                <div style={{ marginTop: '16px' }}>
                  <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '8px' }}>Pontuação por Etapa Alcançada (Chave Secundária)</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>Campeão (Sec)</label>
                      <input type="number" style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }} value={tempConfig.tournamentRules?.stagePointsSecondary?.champion ?? 0} onChange={(e) => setTempConfig({ ...tempConfig, tournamentRules: { ...(tempConfig.tournamentRules || { tiebreakerPriority: ['WINS'] }), stagePointsSecondary: { ...(tempConfig.tournamentRules?.stagePointsSecondary || {}), champion: Number(e.target.value) } } as any })} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>Vice (Sec)</label>
                      <input type="number" style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }} value={tempConfig.tournamentRules?.stagePointsSecondary?.finalist ?? 0} onChange={(e) => setTempConfig({ ...tempConfig, tournamentRules: { ...(tempConfig.tournamentRules || { tiebreakerPriority: ['WINS'] }), stagePointsSecondary: { ...(tempConfig.tournamentRules?.stagePointsSecondary || {}), finalist: Number(e.target.value) } } as any })} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>Semis (Sec)</label>
                      <input type="number" style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }} value={tempConfig.tournamentRules?.stagePointsSecondary?.semiFinalist ?? 0} onChange={(e) => setTempConfig({ ...tempConfig, tournamentRules: { ...(tempConfig.tournamentRules || { tiebreakerPriority: ['WINS'] }), stagePointsSecondary: { ...(tempConfig.tournamentRules?.stagePointsSecondary || {}), semiFinalist: Number(e.target.value) } } as any })} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>Quartas (Sec)</label>
                      <input type="number" style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }} value={tempConfig.tournamentRules?.stagePointsSecondary?.quarterFinalist ?? 0} onChange={(e) => setTempConfig({ ...tempConfig, tournamentRules: { ...(tempConfig.tournamentRules || { tiebreakerPriority: ['WINS'] }), stagePointsSecondary: { ...(tempConfig.tournamentRules?.stagePointsSecondary || {}), quarterFinalist: Number(e.target.value) } } as any })} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {competitionType === 'RANKING' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h5 style={{ margin: 0, color: 'var(--primary-color)', fontSize: '14px' }}>Regras de Ranking Contínuo</h5>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Regra de Desafio</label>
                  <select 
                    style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                    value={(tempConfig.ladderRules?.challengeWindowAbove || 0) > 0 ? 'MAX_POSITIONS_UP' : 'FREE'}
                    onChange={(e) => setTempConfig({ ...tempConfig, ladderRules: { ...(tempConfig.ladderRules || {}), challengeWindowAbove: e.target.value === 'FREE' ? 0 : 3 } as any})}
                  >
                    <option style={{ color: 'black' }} value="FREE">Livre (Qualquer um)</option>
                    <option style={{ color: 'black' }} value="MAX_POSITIONS_UP">Máx. de Posições Acima</option>
                  </select>
                </div>
                {(tempConfig.ladderRules?.challengeWindowAbove || 0) > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Posições Acima Permitidas</label>
                    <input 
                      type="number"
                      style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                      value={tempConfig.ladderRules?.challengeWindowAbove || 3}
                      onChange={(e) => setTempConfig({ ...tempConfig, ladderRules: { ...(tempConfig.ladderRules || {}), challengeWindowAbove: Number(e.target.value) } as any})}
                    />
                  </div>
                )}
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Lógica de Impacto</label>
                  <select 
                    style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                    value={tempConfig.ladderRules?.impactLogic ?? 'LEAPFROG_PUSH'}
                    onChange={(e) => setTempConfig({ ...tempConfig, ladderRules: { ...(tempConfig.ladderRules || {}), impactLogic: e.target.value as any } as any})}
                  >
                    <option style={{ color: 'black' }} value="LEAPFROG_PUSH">Leapfrog (Toma a vaga e empurra)</option>
                    <option style={{ color: 'black' }} value="SWAP_POSITIONS">Troca de Posições (Swap)</option>
                    <option style={{ color: 'black' }} value="POINTS_BASED">Baseado em Pontos (Elo/ATP)</option>
                  </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Criação de Partidas</label>
                  <select 
                    style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                    value={tempConfig.ladderRules?.matchGenerationMode ?? 'MANAGER_ONLY'}
                    onChange={(e) => setTempConfig({ ...tempConfig, ladderRules: { ...(tempConfig.ladderRules || {}), matchGenerationMode: e.target.value as any } as any})}
                  >
                    <option style={{ color: 'black' }} value="MANAGER_ONLY">Manual (Apenas Gestor)</option>
                    <option style={{ color: 'black' }} value="PLAYER_CHALLENGE">Desafio entre Jogadores</option>
                    <option style={{ color: 'black' }} value="AUTO_DRAW">Sorteio Automático (Ranking/Sorteio)</option>
                  </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Partidas por participante / rodada</label>
                  <input 
                    type="number" min="1" max="10"
                    style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                    value={tempConfig.ladderRules?.matchesPerPlayerPerRound ?? 1}
                    onChange={(e) => setTempConfig({ ...tempConfig, ladderRules: { ...(tempConfig.ladderRules || {}), matchesPerPlayerPerRound: Number(e.target.value) } as any})}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Prazo Aceite Desafio (Dias)</label>
                  <input 
                    type="number" min="1" max="14"
                    style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                    value={tempConfig.ladderRules?.acceptDeadlineDays ?? 2}
                    onChange={(e) => setTempConfig({ ...tempConfig, ladderRules: { ...(tempConfig.ladderRules || {}), acceptDeadlineDays: Number(e.target.value) } as any})}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Aprovação Automática (Dias)</label>
                  <input 
                    type="number" min="1" max="90"
                    style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                    value={tempConfig.ladderRules?.inactivityDaysLimit ?? 7}
                    onChange={(e) => setTempConfig({ ...tempConfig, ladderRules: { ...(tempConfig.ladderRules || {}), inactivityDaysLimit: Number(e.target.value) } as any})}
                  />
                </div>
              </div>
            </div>
          )}


          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
            <button onClick={resetForm} style={{ padding: '8px 16px', borderRadius: '6px', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white', cursor: 'pointer' }}>
              Cancelar
            </button>
            <button onClick={handleSave} style={{ padding: '8px 16px', borderRadius: '6px', background: 'var(--primary-color)', border: 'none', color: 'white', fontWeight: 600, cursor: 'pointer' }}>
              Salvar Categoria
            </button>
          </div>
        </div>
      ) : (
        <button 
          onClick={() => { resetForm(); setIsAdding(true); }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px dashed rgba(255,255,255,0.3)', color: 'white', cursor: 'pointer', fontWeight: 600 }}
        >
          <Plus size={18} />
          Adicionar Nova Categoria
        </button>
      )}
      {cropperImageSrc && (
        <ImageCropperModal
          isOpen={!!cropperImageSrc}
          imageSrc={cropperImageSrc}
          onClose={() => setCropperImageSrc(null)}
          onCropComplete={(croppedBase64) => {
            setLogo(croppedBase64);
            setCropperImageSrc(null);
          }}
        />
      )}
    </div>
  );
};
